import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import './Page2.css';
import backgroundImage from '../images/page-2.jpg';
import backgroundMobile from '../images/page-2-mobile.jpg';

// === Tooltip-Positionierung wie in Page-1 (obj-fit:cover-aware) ===
const P2_ORIGINAL_W = 6000;
const P2_ORIGINAL_H = 3374;

function rectForCoverToPercent([x1, y1, x2, y2], containerW, containerH) {
    const scale = Math.max(containerW / P2_ORIGINAL_W, containerH / P2_ORIGINAL_H);
    const renderedW = P2_ORIGINAL_W * scale;
    const renderedH = P2_ORIGINAL_H * scale;
    const offsetX = (containerW - renderedW) / 2;
    const offsetY = (containerH - renderedH) / 2;

    const rx1 = x1 * scale + offsetX;
    const ry1 = y1 * scale + offsetY;
    const rw = (x2 - x1) * scale;
    const rh = (y2 - y1) * scale;

    return {
        left: `${(rx1 / containerW) * 100}%`,
        top: `${(ry1 / containerH) * 100}%`,
        width: `${(rw / containerW) * 100}%`,
        height: `${(rh / containerH) * 100}%`,
    };
}

// 1×1-Punkt (Startpunkt) pro Projekt im Originalbild
const TOOLTIP_RECTS_PX = {
    projekt1: [2450, 220, 2451, 221],
    projekt2: [2450, 220, 2451, 221],
    projekt3: [4420, 1160, 4421, 1161],
    projekt4: [4420, 1160, 4421, 1161],
};

// Feinoffset addieren (derzeit 0)
const TOOLTIP_OFFSETS = {
    projekt1: { dx: 0, dy: 0 },
    projekt2: { dx: 0, dy: 0 },
    projekt3: { dx: 0, dy: 0 },
    projekt4: { dx: 0, dy: 0 },
};
const TOOLTIP_OFFSETS_MOBILE = {
    projekt1: { dx: -16, dy: 10 },
    projekt2: { dx: -16, dy: 10 },
    projekt3: { dx: 130, dy: 0 },
    projekt4: { dx: 130, dy: 0 },
};
function usePointerEnv() {
    const [isCoarsePointer, setIsCoarsePointer] = useState(false);

    useEffect(() => {
        const hasTouchLike = () =>
            (typeof window !== 'undefined' && 'ontouchstart' in window) ||
            (typeof navigator !== 'undefined' &&
                ((navigator.maxTouchPoints ?? 0) > 0 ||
                    (navigator.msMaxTouchPoints ?? 0) > 0));

        let mq;
        if (typeof window !== 'undefined' && window.matchMedia) {
            mq = window.matchMedia('(pointer: coarse)');
        }

        const update = () => {
            setIsCoarsePointer((mq && mq.matches) || hasTouchLike());
        };

        update();

        const onChange = () => update();
        mq?.addEventListener?.('change', onChange);
        window.addEventListener?.('resize', onChange);
        window.addEventListener?.('touchstart', onChange, { passive: true });

        return () => {
            mq?.removeEventListener?.('change', onChange);
            window.removeEventListener?.('resize', onChange);
            window.removeEventListener?.('touchstart', onChange);
        };
    }, []);

    return { isCoarsePointer };
}

/* =========================
   DESKTOP (klassisch, kein Scroll)
========================= */
const areas = [
    {
        id: 'projekt1',
        clipPath:
            'polygon(12.1% 0.03%, 12.8% 99.63%, 32.95% 99.49%, 32.42% 61.85%, 38.6% 52.27%, 38.95% 0.47%)',
        labelHtml: (
            <div className="tooltip-content">
                <div className="red font-20 bold">eVolo Skyscraper</div>
                <div className="red font-25 extra-bold">Competition</div>
                <div className="black font-31">Naval Steel</div>
                <div className="black font-21">Structures turned</div>
                <div className="black font-39">Highrises</div>
                <div className="black font-39">Hamburg</div>
                <div className="black-bold font-24">www.evolo.us</div>
            </div>
        ),
    },
    {
        id: 'projekt2',
        clipPath:
            'polygon(60.23% 0%, 39.48% 0.15%, 39.13% 52.27%, 33.2% 61.99%, 33.65% 99.49%, 52.47% 98.93%, 52.73% 61.68%, 60.15% 52.87%)',
        labelHtml: (
            <div className="tooltip-content">
                <div className="heading-red">STAIRCASE APARTMENTS</div>
                <div className="subheading-black">Alexanderplatz Berlin</div>
                <div className="text-normal">Competition upon invitat-</div>
                <div className="text-normal-large">ion of www.plattformnach-</div>
                <div className="text-normal space">wuchsarchitekten.de</div>
                <div className="text-normal-small">
                    <span className="red-bold">Second prize.</span> Exhibitions:
                </div>
                <div className="text-normal">Berlin: Aedes Prenzlauer </div>
                <div className="text-normal spacing-025">
                    Berg, K&ouml;ln: KAP-Forum
                </div>
            </div>
        ),
    },
    {
        id: 'projekt3',
        clipPath:
            'polygon(60.68% 38.01%, 69.38% 38.35%, 73.85% 53.79%, 73.75% 98.84%, 52.82% 99.34%, 52.9% 62.58%, 60.5% 52.94%)',
        labelHtml: (
            <div className="tooltip-content">
                <div className="red font-19">THE ROOSEVELT ISLAND</div>
                <div className="red font-17-8">UNIVERSAL ARTS CENTER</div>
                <div className="subheading-black">Manhattan, New York</div>
                <div className="black font-23-8">Competition upon invitat-</div>
                <div className="black font-22">ion of the AIANY Emerging</div>
                <div className="black font-22">New York Architects Com-</div>
                <div className="text-normal">mittee ENYA</div>
            </div>
        ),
    },
    {
        id: 'projekt4',
        clipPath:
            'polygon(84.53% 42.97%, 84.53% 99.34%, 74.28% 98.84%, 74.02% 42.71%)',
        labelHtml: (
            <div className="tooltip-content">
                <div className="heading-red">THE SPATIAL APERTURE</div>
                <div className="black font-56">Furniture</div>
                <div className="black font-27">Convertible Seat and</div>
                <div className="black font-25-spacing">Shell controls opening</div>
                <div className="black font-25-extra-space">
                    and turns seat into
                </div>
                <div className="black font-27">lounger</div>
            </div>
        ),
    },
];

function Desktop() {
    const [hoveredId, setHoveredId] = useState(null);
    const wrapperRef = useRef(null);
    const [labelBoxes, setLabelBoxes] = useState({});

    const wrapper = {
        position: 'relative',
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        cursor: 'default',
        paddingTop: '3em',
    };

    const imgStyle = {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        pointerEvents: 'none',
    };
    useLayoutEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;

        const recompute = () => {
            const w = el.clientWidth;
            const h = el.clientHeight;
            

            const imgScale = Math.max(w / P2_ORIGINAL_W, h / P2_ORIGINAL_H);
            // Smartphones ausschließen:
            if (window.innerWidth > 768) {
                // → Mobile-Mode, also niemals ausblenden
            } else {
                // Nur Desktop & Tablet
                const MIN_HEIGHT_FOR_LABELS = 500;
                if (h < MIN_HEIGHT_FOR_LABELS) {
                    setLabelBoxes({});
                    return;
                }
            }
            // 🔹 1. Wenn das Fenster SEHR klein ist → gar keine Tooltips
            //if (w < 1200) {
            //    setLabelBoxes({});
            //    return;
            //}
            
            // 🔹 2. Stufige Skalierung nach Fensterbreite
            let labelScale;
            if (w >= 5120) {
                // sehr große
                labelScale = 1.7;
            } else if (w >= 2600) {
                // großer iMac, Vollbild
                labelScale = 1.2;
            } else if (w >= 2000) {
                labelScale = 0.84;
            } else if (w >= 1600) {
                // „normales“ großes Fenster
                labelScale = 0.7;     // dein bisheriger Sweet-Spot
            }
            else if (w >= 1350) {
                labelScale = 0.5;
            } else if (w >= 1300) {
                labelScale = 0.6;

            }else if (w < 1200) {
                labelScale = 0.4; //TEST

            } else {
                // knapp unter 1300, aber noch >= 1200
                labelScale = 0.45;
            }

            // 🔹 3. Breite der Textbox (kann so bleiben)
            const baseLabelWidth = 320;
            const labelWidthPx = Math.min(
                Math.max(baseLabelWidth * imgScale, 320),
                420
            );

            const next = {};
            Object.entries(TOOLTIP_RECTS_PX).forEach(([id, rectPx]) => {
                const base = rectForCoverToPercent(rectPx, w, h);
                const { dx = 0, dy = 0 } = TOOLTIP_OFFSETS[id] || {};
                const dxPx = dx * imgScale;
                const dyPx = dy * imgScale;

                const leftPct = parseFloat(base.left);
                const topPct = parseFloat(base.top);

                next[id] = {
                    position: 'absolute',
                    left: `calc(${leftPct}% + ${dxPx}px)`,
                    top: `calc(${topPct}% + ${dyPx}px)`,
                    width: `${labelWidthPx}px`,
                    maxWidth: '30vw',
                    minWidth: '240px',
                    pointerEvents: 'none',
                    transformOrigin: 'top left',
                    transform: `scale(${labelScale})`,
                    willChange: 'transform',
                };
            });

            setLabelBoxes(next);
        };

        recompute();

        let ro;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(recompute);
            ro.observe(el);
        } else {
            window.addEventListener('resize', recompute);
        }
        return () => {
            if (ro) ro.disconnect();
            else window.removeEventListener('resize', recompute);
        };
    }, []);

    return (
        <div ref={wrapperRef} style={wrapper}>
            <img src={backgroundImage} alt="Page 2" style={imgStyle} />

            {areas.map((area) => (
                <div
                    key={area.id}
                    className={`hover-area ${hoveredId === area.id ? 'hovered' : ''
                        }`}
                    style={{ clipPath: area.clipPath }}
                    onMouseEnter={() => setHoveredId(area.id)}
                    onMouseLeave={() => setHoveredId(null)}
                />
            ))}

            {areas.map((area) =>
                hoveredId === area.id ? (
                    <div
                        key={`${area.id}-label`}
                        className={`hover-label ${area.id}`}
                        style={labelBoxes[area.id]}
                    >
                        {area.labelHtml}
                    </div>
                ) : null
            )}
        </div>
    );
}

/* =========================
   MOBILE — (rechts starten, Image-Map, fixed Tooltip)
========================= */

const MOBILE = { width: 1536, height: 1096 };

const mobileAreas = [
    {
        id: 'projekt1',
        shape: 'poly',
        coords: '0,1096,446,1094,438,680,574,582,601,56,0,54',
    },
    {
        id: 'projekt2',
        shape: 'poly',
        coords:
            '985,580,844,687,837,1098,448,1094,442,680,577,581,609,57,990,54',
    },
    {
        id: 'projekt3',
        shape: 'poly',
        coords: '989,579,990,473,1153,469,1197,535,1194,1093,843,1098,844,691',
    },
    { id: 'projekt4', shape: 'rect', coords: '1201,1091,1483,531' },
];


const areasMobile = [
    {
        id: 'projekt1',
        clipPath:
            'polygon(1.1% 0.03%, 1.8% 99.63%, 27.95% 101.49%, 27.42% 60.85%, 37.6% 51.27%, 37.95% 0.47%)',
        labelHtml: areas.find(a => a.id === 'projekt1')?.labelHtml,
    },
    {
        id: 'projekt2',
        clipPath: 'polygon(65.23% 2%, 40.48% -1.85%, 38.13% 52.27%, 29.2% 60.99%, 29.65% 104.49%, 52.47% 105.93%, 55.73% 67.68%, 63.15% 52.87%)',
        labelHtml: areas.find(a => a.id === 'projekt2')?.labelHtml,
    },
    {
        id: 'projekt3',
        clipPath: 'polygon(66.68% 42.01%, 79.38% 43.35%, 79.85% 53.79%, 79.75% 105.84%, 54.82% 101.34%, 56.9% 68.58%, 62.5% 55.94%)',
        labelHtml: areas.find(a => a.id === 'projekt3')?.labelHtml,
    },
    {
        id: 'projekt4',
        clipPath:
            'polygon(96.53% 48.97%, 97.53% 131.34%, 81.28% 104.84%, 81.02% 48.71%)',
        labelHtml: areas.find(a => a.id === 'projekt4')?.labelHtml,
    },
];

function Mobile() {
    const [hoveredId, setHoveredId] = useState(null);
    const containerRef = useRef(null);
    const [state, setState] = useState({ w: 0, h: 0, scale: 1 });
    const [labelBoxes, setLabelBoxes] = useState({});

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const recompute = () => {
            const h = el.clientHeight;

            // Bild skaliert wie im alten Mobile: Höhe = 100vh
            const scale = h / MOBILE.height;
            const w = Math.round(MOBILE.width * scale);

            setState({ w, h, scale });

            // rechts starten (wie vorher)
            el.scrollLeft = Math.max(0, w - el.clientWidth);

            // Tooltips: aus ORIGINAL-Koordinaten → Prozent → Mobile-Koords → skaliert
            const baseLabelWidth = 270;
            const clamp = (x, min, max) => Math.min(max, Math.max(min, x));
            let labelScale = 0.45;


            if (h < 520) {
                labelScale = 0;
            }
            //const labelScale = 0.45;  Mobile etwas kleiner
            const labelWidthPx = clamp(baseLabelWidth * scale, 270, 360);

            const next = {};

            Object.entries(TOOLTIP_RECTS_PX).forEach(([id, rectPx]) => {
                const [ox1, oy1] = rectPx;

                // 1) original → Prozent im großen Bild
                const px = ox1 / P2_ORIGINAL_W;
                const py = oy1 / P2_ORIGINAL_H;

                // 2) Prozent → Pixel im MOBILE-Bild
                const mx = px * MOBILE.width;
                const my = py * MOBILE.height;

                // 3) MOBILE-Pixel → gerenderte Pixel
                const leftPx = mx * scale;
                const topPx = my * scale;

                const { dx = 0, dy = 0 } = TOOLTIP_OFFSETS_MOBILE[id] || {};
                const dxPx = dx * scale;
                const dyPx = dy * scale ;

                next[id] = {
                    position: 'absolute',
                    left: `${Math.round(leftPx + dxPx)}px`,
                    top: `${Math.round(topPx + dyPx)}px`,
                    width: `${Math.round(labelWidthPx)}px`,
                    transformOrigin: 'top left',
                    transform: `scale(${labelScale})`,
                    pointerEvents: 'none',
                    willChange: 'transform',
                };
            });

            setLabelBoxes(next);
        };

        recompute();
        const ro = new ResizeObserver(recompute);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const activate = (id) => setHoveredId(id);
    const deactivate = () => setHoveredId(null);

    return (
        <div
            ref={containerRef}
            style={{
                width: '100vw',
                height: '100vh',
                overflowX: 'auto',
                overflowY: 'hidden',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'auto',
                position: 'relative',
                background: '#000',
            }}
            onClick={(e) => {
                if (!(e.target instanceof HTMLElement)) return;
                if (!e.target.closest('.hover-area')) deactivate();
            }}
        >
            <div
                style={{
                    position: 'relative',
                    width: `${state.w}px`,
                    height: '100%',
                    margin:'0 auto',
                }}
            >
                <img
                    src={backgroundMobile}
                    alt="Page 2 (mobile)"
                    style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '101%',
                        width: 'auto',
                        display: 'block',
                        pointerEvents: 'none',
                        userSelect: 'none',
                    }}
                    fetchPriority="high"
                    decoding="async"
                    draggable={false}
                />

                {areasMobile.map((area) => (
                    <div
                        key={area.id}
                        className={`hover-area ${hoveredId === area.id ? 'hovered' : ''}`}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            //background: '#ffff0061',
                            clipPath: area.clipPath,
                        }}
                        onClick={(e) => {
                            e.preventDefault();
                            activate(area.id);
                        }}
                        onTouchEnd={() => activate(area.id)}
                    />
                ))}

                {areasMobile.map((area) =>
                    hoveredId === area.id ? (
                        <div
                            key={`${area.id}-label`}
                            className={`hover-label mobile ${area.id}`}
                            style={labelBoxes[area.id]}
                        >
                            {area.labelHtml}
                        </div>
                    ) : null
                )}
            </div>
        </div>
    );
}


/* =========================
   DESKTOP + TABLET SCROLL-VARIANTE
========================= */

function DesktopTablet() {
    const [hoveredId, setHoveredId] = useState(null);
    const containerRef = useRef(null);
    const [state, setState] = useState({ w: 0, h: 0, scale: 1 });
    const [labelBoxes, setLabelBoxes] = useState({});

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const recompute = () => {
            const h = el.clientHeight;
       



            const scale = h / P2_ORIGINAL_H;
            const w = Math.round(P2_ORIGINAL_W * scale);

            setState({ w, h, scale });

            el.scrollLeft = Math.max(0, w - el.clientWidth);
            if (window.innerWidth > 768) {
                // → Mobile-Mode, also niemals ausblenden
            } else {
                // Nur Desktop & Tablet
                const MIN_HEIGHT_FOR_LABELS = 500;
                if (h < MIN_HEIGHT_FOR_LABELS) {
                    setLabelBoxes({});
                    return;
                }
            }
            const clamp = (x, min, max) => Math.min(max, Math.max(min, x));
             
            let labelScale;
            

            if (h < 520) {
                labelScale = 0;
            }
            else if (w >= 768) {
                labelScale = 0.4;
            } else {
                labelScale = 0.25;
            }



            const baseLabelWidth = 270;
            const labelWidthPx = clamp(baseLabelWidth * scale, 270, 420);

            const next = {};
            Object.entries(TOOLTIP_RECTS_PX).forEach(([id, rectPx]) => {
                const [x1, y1] = rectPx;
                const { dx = 0, dy = 0 } = TOOLTIP_OFFSETS[id] || {};
                const leftPx = (x1 + dx) * scale;
                const topPx = (y1 + dy) * scale;

                next[id] = {
                    position: 'absolute',
                    left: `${Math.round(leftPx)}px`,
                    top: `${Math.round(topPx)}px`,
                    width: `${Math.round(labelWidthPx)}px`,
                    pointerEvents: 'none',
                    transformOrigin: 'top left',
                    transform: `scale(${labelScale})`,
                    willChange: 'transform',
                };
            });

            setLabelBoxes(next);
        };

        recompute();
        const ro = new ResizeObserver(recompute);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const activate = (id) => setHoveredId(id);
    const deactivate = () => setHoveredId(null);

    return (
        <div
            ref={containerRef}
            style={{
                width: '100vw',
                height: '100vh',
                overflowX: 'auto',
                overflowY: 'hidden',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'auto',
                position: 'relative',
                background: '#000',
                margin: '0 auto',
            }}
            onMouseLeave={deactivate}
            onClick={(e) => {
                if (!(e.target instanceof HTMLElement)) return;
                if (!e.target.closest('.hover-area')) setHoveredId(null);
            }}
        >
            <div
                style={{
                    position: 'relative',
                    width: `${state.w}px`,
                    height: '100%',
                    margin: '0 auto',
                }}
            >
                <img
                    src={backgroundImage}
                    alt="Page 2"
                    style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: 'auto',
                        display: 'block',
                        pointerEvents: 'none',
                        userSelect: 'none',
                    }}
                    fetchPriority="high"
                    decoding="async"
                    draggable={false}
                />

                {areas.map((area) => (
                    <div
                        key={area.id}
                        className={`hover-area ${hoveredId === area.id ? 'hovered' : ''
                            }`}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            clipPath: area.clipPath,
                        }}
                        onMouseEnter={() => activate(area.id)}
                        onMouseLeave={deactivate}
                        onTouchEnd={() => activate(area.id)}
                        onClick={(e) => {
                            e.preventDefault();
                            activate(area.id);
                        }}
                    />
                ))}

                {areas.map((area) =>
                    hoveredId === area.id ? (
                        <div
                            key={`${area.id}-label`}
                            className={`hover-label ${area.id}`}
                            style={labelBoxes[area.id]}
                        >
                            {area.labelHtml}
                        </div>
                    ) : null
                )}
            </div>
        </div>
    );
}

/* =========================
   RESPONSIVE SWITCH
========================= */

export default function Page2() {
    const [vw, setVw] = React.useState(
        () => (typeof window !== 'undefined' ? window.innerWidth : 1920)
    );

    const { isCoarsePointer } = usePointerEnv();

    React.useEffect(() => {
        const onResize = () => setVw(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const isNarrow = vw < 768;
    const isTabletBand = vw >= 768 && vw <= 1024;

    // < 768 px:
    //   - Touch  → Mobile (Scroll + fixed Tooltip oben links)
    //   - Maus   → DesktopTablet
    if (isNarrow) {
        if (isCoarsePointer) {
            return <Mobile />;
        } else {
            return <DesktopTablet />;
        }
    }

    // 768 – 1024 → immer DesktopTablet
    if (isTabletBand) {
        return <DesktopTablet />;
    }

    // > 1024 → klassischer Desktop
    return <Desktop />;
}
